import { HelmetDexteel } from "@dexteel/mesf-core";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import {
	PointerEvent as ReactPointerEvent,
	SyntheticEvent,
	useState,
} from "react";
import { DrawingModal } from "../../components/DrawingModal";

type Props = {
	url: string | null;
	fileName: string | null;
	sectionName: string;
	rev: number;
	width: number;
	canResize: boolean;
	onWidthChange: (width: number, isFinal?: boolean) => void;
	onRatioChange: (ratio: number) => void;
	onResetSize: () => void;
};

export const DrawingPanel = ({
	url,
	fileName,
	sectionName,
	rev,
	width,
	canResize,
	onWidthChange,
	onRatioChange,
	onResetSize,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	if (!url)
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					minHeight: 96,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography variant="caption" color="text.secondary">
					No profile drawing
				</Typography>
			</Box>
		);

	const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
		event.preventDefault();

		const startX = event.clientX;
		const startWidth = width;
		let lastWidth = startWidth;

		const onMove = (moveEvent: globalThis.PointerEvent) => {
			lastWidth = startWidth + (startX - moveEvent.clientX);
			onWidthChange(lastWidth);
		};

		const onUp = () => {
			document.body.style.userSelect = "";
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("pointerup", onUp);
			onWidthChange(lastWidth, true);
		};

		document.body.style.userSelect = "none";
		window.addEventListener("pointermove", onMove);
		window.addEventListener("pointerup", onUp);
	};

	return (
		<>
			<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
				<Tooltip title={fileName ?? "Profile drawing"} followCursor>
					<Box
						component="img"
						src={url}
						alt={`${sectionName} profile drawing`}
						onClick={() => setIsOpen(true)}
						onLoad={(event: SyntheticEvent<HTMLImageElement>) => {
							const { naturalWidth, naturalHeight } = event.currentTarget;
							if (naturalWidth > 0 && naturalHeight > 0)
								onRatioChange(naturalWidth / naturalHeight);
						}}
						sx={{
							display: "block",
							width: "100%",
							height: "100%",
							minHeight: 96,
							objectFit: "contain",
							cursor: "zoom-in",
							backgroundColor: "#fff",
							p: 1,
							pl: 1.5,
						}}
					/>
				</Tooltip>

				{canResize && (
					<Box
						onPointerDown={startResize}
						onDoubleClick={onResetSize}
						title="Drag to resize the drawing (double click to reset)"
						sx={{
							position: "absolute",
							top: 0,
							bottom: 0,
							left: 0,
							width: 8,
							cursor: "ew-resize",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							touchAction: "none",
							zIndex: 1,
							"&:hover": { backgroundColor: "action.hover" },
							"&::after": {
								content: '""',
								width: 2,
								height: 28,
								borderRadius: 1,
								backgroundColor: "divider",
							},
						}}
					/>
				)}

				<IconButton
					size="small"
					title="Expand"
					onClick={() => setIsOpen(true)}
					sx={{
						position: "absolute",
						top: 4,
						right: 4,
						backgroundColor: "background.paper",
						border: "1px solid",
						borderColor: "divider",
						"&:hover": { backgroundColor: "background.paper" },
					}}
				>
					<OpenInFullIcon sx={{ fontSize: 16 }} />
				</IconButton>
			</Box>

			{isOpen && (
				<HelmetDexteel title={`Drawing ${sectionName} - Inspection Sheet`} />
			)}
			<DrawingModal
				open={isOpen}
				url={url}
				sectionName={sectionName}
				rev={rev}
				fileName={fileName}
				onClose={() => setIsOpen(false)}
			/>
		</>
	);
};
